import { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode, Suspense } from "react";
import { useLocation } from "react-router-dom";
import { importWithRetry } from "@/lib/lazyWithRetry";

type Language = "de" | "en";

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Locale dictionaries are split into per-language chunks so visitors only
// download the active language. Loaded lazily and cached. During
// prerendering (LovableHTML), <Suspense> awaits the promise so the
// emitted HTML always contains resolved translations.
const loaders: Record<Language, () => Promise<Record<string, string>>> = {
  de: () => importWithRetry(() => import("@/locales/de")).then((m) => m.default),
  en: () => importWithRetry(() => import("@/locales/en")).then((m) => m.default),
};

const cache: Partial<Record<Language, Record<string, string>>> = {};
const inflight: Partial<Record<Language, Promise<void>>> = {};

function getDict(lang: Language): Record<string, string> {
  const cached = cache[lang];
  if (cached) return cached;
  let promise = inflight[lang];
  if (!promise) {
    promise = loaders[lang]().then((dict) => {
      cache[lang] = dict;
    });
    inflight[lang] = promise;
  }
  // Suspense: throw the promise so React waits before rendering.
  throw promise;
}

// Inner component that suspends until the active dictionary is loaded.
function LanguageGate({
  language,
  setLanguage,
  children,
}: {
  language: Language;
  setLanguage: (lang: Language) => void;
  children: ReactNode;
}) {
  const dict = getDict(language); // suspends on first call per language

  const t = useCallback(
    (key: string): string => dict[key] || key,
    [dict],
  );

  const value = useMemo(
    () => ({ language, setLanguage, t }),
    [language, setLanguage, t],
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  // Persisted/fallback language. Used only on non-localized routes
  // (e.g. /admin) where the URL has no /de or /en prefix.
  const [stored, setStoredState] = useState<Language>(() => {
    if (typeof window !== "undefined") {
      const path = window.location.pathname;
      if (path === "/en" || path.startsWith("/en/")) return "en";
      if (path === "/de" || path.startsWith("/de/")) return "de";
    }
    const isManual =
      typeof localStorage !== "undefined" &&
      localStorage.getItem("language_manual") === "true";
    if (isManual) {
      const saved = localStorage.getItem("language") as Language | null;
      if (saved === "de" || saved === "en") return saved;
    }
    return "de";
  });

  // URL is the synchronous source of truth on every /de/* and /en/*
  // route. Reading useLocation() here means children mount with the
  // correct language on the very first render — no effect, no flicker.
  const { pathname } = useLocation();
  const urlLocale: Language | null =
    pathname === "/de" || pathname.startsWith("/de/")
      ? "de"
      : pathname === "/en" || pathname.startsWith("/en/")
        ? "en"
        : null;

  const language: Language = urlLocale ?? stored;

  // One-time cleanup: remove legacy language values that were not manually set
  useEffect(() => {
    if (localStorage.getItem("language_manual") !== "true") {
      localStorage.removeItem("language");
      localStorage.removeItem("language_detected");
    }
  }, []);

  // Persistence-only sync: when the URL locale changes, mirror it into
  // the stored fallback so non-localized routes (e.g. /admin) continue
  // to render in the user's most recent locale.
  useEffect(() => {
    if (urlLocale && urlLocale !== stored) {
      setStoredState(urlLocale);
    }
  }, [urlLocale, stored]);

  const setLanguage = useCallback((lang: Language) => {
    if (lang !== "de" && lang !== "en") return;
    setStoredState((prev) => {
      if (prev === lang) return prev;
      try {
        localStorage.setItem("language", lang);
        localStorage.setItem("language_manual", "true");
      } catch {
        // ignore storage errors (private mode, etc.)
      }
      return lang;
    });
  }, []);

  return (
    <Suspense fallback={null}>
      <LanguageGate language={language} setLanguage={setLanguage}>
        {children}
      </LanguageGate>
    </Suspense>
  );
}

// Default context for HMR resilience and the rare case `useLanguage` is
// called outside the provider. Returns the key as a fallback string.
const defaultContext: LanguageContextType = {
  language: "de",
  setLanguage: () => {},
  t: (key: string) => {
    const dict = cache.de;
    return (dict && dict[key]) || key;
  },
};

export function useLanguage() {
  const context = useContext(LanguageContext);
  // Return default context during HMR cycles to prevent crashes
  if (context === undefined) {
    console.warn("useLanguage called outside LanguageProvider - using default context");
    return defaultContext;
  }
  return context;
}
